import {
  getSortedGoals,
  useCreateUnlimitedGoalsAllowed,
  useGoalControls,
  useGoals,
} from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import {
  useGoalSorting,
  useUpdateGoalOrder,
  useUserHadTrial,
} from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GoalListSection,
  SelfSortingGoalListSection,
} from 'shared/components/ui/goal-list-section';
import { GoalPremiumBanner } from 'shared/components/ui/goal-premium-banner';
import { Section, Sizes } from 'shared/components/ui/section';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { GoalSortingMode } from 'shared/types/sorting-mode';
import { GoalRoadmapSortingKeys } from 'shared/types/user-settings';

import { useActiveGoals } from '../../hooks/use-active-goals';
import { GoalsFilterOptions, goalsFilters } from '../../utils/goals-filter';
import { EmptyBanner } from '../empty-banner';
import * as Styled from './no-grouping-goal-list.style';

export type NoGroupingGoalListProps = {};

export const NoGroupingGoalList: React.FC<NoGroupingGoalListProps> = () => {
  const user = useUser();
  const today = useToday();
  const allGoals = useGoals();
  const { t } = useTranslation();
  const lifeAreas = useLocalizedLifeAreas();
  const userHadTrial = useUserHadTrial();

  const updateOrder = useUpdateGoalOrder(GoalRoadmapSortingKeys.InProgress);

  const openPremiumDialog = useOpenPremiumDialog();
  const {
    allowed: createGoalAllowed,
    enforced: createGoalEnforced,
    limit: createGoalLimit,
    total: createGoalTotal,
  } = useCreateUnlimitedGoalsAllowed();
  const goalSorting = useGoalSorting(GoalRoadmapSortingKeys.InProgress);
  const { goals: activeGoals } = useActiveGoals();
  const { onUpdateGoalProgress } = useGoalControls(activeGoals);

  const sortedGoals = useMemo(
    () =>
      getSortedGoals(activeGoals, {
        mode: [user?.settings?.goalSortingMode].filter(
          Boolean,
        ) as GoalSortingMode[],
        order: goalSorting,
        allGoals,
      }),
    [allGoals, goalSorting, activeGoals, user?.settings?.goalSortingMode],
  );

  const completedGoals = useMemo(
    () =>
      goalsFilters[GoalsFilterOptions.Completed](allGoals, today).sort(
        (goalA, goalB) =>
          goalB.completedAt!.getTime() - goalA.completedAt!.getTime(),
      ),
    [allGoals, today],
  );

  const archivedGoals = useMemo(
    () =>
      goalsFilters[GoalsFilterOptions.Archived](allGoals, today).sort(
        (goalA, goalB) =>
          goalB.archivedAt!.getTime() - goalA.archivedAt!.getTime(),
      ),
    [allGoals, today],
  );

  const showPremiumBanner =
    !createGoalAllowed ||
    (!createGoalEnforced && createGoalLimit < createGoalTotal);

  return !sortedGoals.length ? (
    <EmptyBanner />
  ) : (
    <>
      <Styled.Container>
        <SelfSortingGoalListSection
          lifeAreas={lifeAreas}
          goals={sortedGoals}
          allGoals={allGoals}
          onPremium={openPremiumDialog}
          onReorder={updateOrder}
          onUpdateProgress={onUpdateGoalProgress}
        />

        {showPremiumBanner && (
          <GoalPremiumBanner
            onClick={openPremiumDialog}
            createGoalEnforced={createGoalEnforced}
            createGoalTotal={createGoalTotal}
            createGoalLimit={createGoalLimit}
            hadTrial={userHadTrial}
          />
        )}
      </Styled.Container>
      {!!completedGoals.length && (
        <Section
          title={t('pages.goals.completed.title')}
          titleCount={completedGoals.length}
          size={Sizes.Small}
          canCollapse
        >
          <Styled.CompletedList>
            <GoalListSection
              goals={completedGoals}
              allGoals={allGoals}
              lifeAreas={lifeAreas}
              onPremium={openPremiumDialog}
              onUpdateProgress={onUpdateGoalProgress}
            />
          </Styled.CompletedList>
        </Section>
      )}

      {!!archivedGoals.length && (
        <Section
          title={t('pages.goals.archived.title')}
          titleCount={archivedGoals.length}
          size={Sizes.Small}
          canCollapse
        >
          <Styled.CompletedList>
            <GoalListSection
              goals={archivedGoals}
              allGoals={allGoals}
              lifeAreas={lifeAreas}
              onPremium={openPremiumDialog}
              onUpdateProgress={onUpdateGoalProgress}
            />
          </Styled.CompletedList>
        </Section>
      )}
    </>
  );
};
